import { useState } from "react";
import { Bars3Icon, ChevronDownIcon } from "@heroicons/react/24/outline";
import dojoLogoDark from "../../assets/dojotrack_logo_icon_png.png";

const SITE = "https://dojotrack.com";

type AuthSiteHeaderProps = {
  appBaseUrl: string;
};

export default function AuthSiteHeader({ appBaseUrl }: AuthSiteHeaderProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);

  const closeMenu = () => {
    setMobileOpen(false);
    setFeaturesOpen(false);
  };

  return (
    <header className={`auth-site-header${mobileOpen ? " is-open" : ""}`}>
      <div className="auth-site-header__bar">
        <a className="auth-site-header__brand" href={SITE} onClick={closeMenu}>
          <span className="auth-logo" aria-hidden="true">
            <img
              className="auth-logo__mark"
              src={dojoLogoDark}
              alt=""
              width={50}
              height={50}
            />
            <span className="auth-logo__word">Dojotrack</span>
          </span>
          <span className="sr-only">DojoTrack home</span>
        </a>

        <button
          type="button"
          className="auth-nav-toggle"
          aria-controls="auth-primary-nav"
          aria-expanded={mobileOpen}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          onClick={() => {
            setMobileOpen((open) => !open);
            setFeaturesOpen(false);
          }}
        >
          <Bars3Icon className="h-[22px] w-[22px]" aria-hidden="true" />
        </button>

        <nav id="auth-primary-nav" className="auth-site-nav" aria-label="Primary">
          <div className="auth-site-nav__panel">
            <ul>
              <li>
                <a href={SITE} onClick={closeMenu}>
                  Home
                </a>
              </li>
              <li
                className={`auth-nav-item--mega${featuresOpen ? " is-open" : ""}`}
                onMouseEnter={() => setFeaturesOpen(true)}
                onMouseLeave={() => setFeaturesOpen(false)}
              >
                <button
                  type="button"
                  className="auth-nav-mega__trigger"
                  aria-expanded={featuresOpen}
                  aria-haspopup="true"
                  aria-controls="auth-nav-mega-features"
                  onClick={() => setFeaturesOpen((open) => !open)}
                >
                  Features
                  <ChevronDownIcon className="auth-nav-mega__chevron" aria-hidden="true" />
                </button>
                <div
                  className="auth-nav-mega"
                  id="auth-nav-mega-features"
                  role="region"
                  aria-label="Features"
                >
                  <div className="auth-nav-mega__panel">
                    <div className="auth-nav-mega__inner">
                      <a
                        className="auth-nav-mega__spot"
                        href={`${SITE}/features/`}
                        onClick={closeMenu}
                      >
                        <div className="auth-nav-mega__shot" aria-hidden="true">
                          <div className="auth-nav-mega__tier">
                            <span className="auth-nav-mega__tier-label">Free</span>
                            <span className="is-on" />
                            <span className="is-on" />
                            <span />
                            <span />
                          </div>
                          <div className="auth-nav-mega__tier is-pro">
                            <span className="auth-nav-mega__tier-label">Premium</span>
                            <span className="is-on" />
                            <span className="is-on" />
                            <span className="is-on" />
                            <span className="is-on" />
                          </div>
                        </div>
                        <span className="auth-nav-mega__spot-copy">
                          <svg
                            className="auth-nav-mega__icon"
                            width="22"
                            height="22"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.75"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M3 3v18h18" />
                            <path d="M7 16v-5M12 16V8M17 16v-9" />
                          </svg>
                          <span>
                            <strong>Compare features</strong>
                            <em>See what’s included from Free to Premium.</em>
                          </span>
                        </span>
                      </a>
                      <div className="auth-nav-mega__plans">
                        <p className="auth-nav-mega__kicker">Plans</p>
                        <ul className="auth-nav-mega__items">
                          <li>
                            <a href={`${SITE}/free-plan/`} onClick={closeMenu}>
                              <span className="auth-nav-mega__icon">
                                <svg
                                  width="22"
                                  height="22"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.75"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true"
                                >
                                  <path d="M20 12v10H4V12" />
                                  <path d="M2 7h20v5H2z" />
                                  <path d="M12 22V7" />
                                  <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
                                  <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
                                </svg>
                              </span>
                              <span>
                                <strong>Free Plan</strong>
                                <em>Student management, leads, and classes — no credit card.</em>
                              </span>
                            </a>
                          </li>
                          <li>
                            <a href={`${SITE}/paid-plans/`} onClick={closeMenu}>
                              <span className="auth-nav-mega__icon">
                                <svg
                                  width="22"
                                  height="22"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.75"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true"
                                >
                                  <path d="M13 2 4 14h7l-1 8 9-12h-7Z" />
                                </svg>
                              </span>
                              <span>
                                <strong>Premium Plans</strong>
                                <em>AI retention, automatic payments, SMS, and the full CRM.</em>
                              </span>
                            </a>
                          </li>
                          <li>
                            <a href={`${SITE}/product-demo/`} onClick={closeMenu}>
                              <span className="auth-nav-mega__icon">
                                <svg
                                  width="22"
                                  height="22"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.75"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true"
                                >
                                  <polygon points="6 4 20 12 6 20 6 4" />
                                </svg>
                              </span>
                              <span>
                                <strong>Watch demo</strong>
                                <em>An 8-minute walkthrough of the full platform.</em>
                              </span>
                            </a>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
              <li>
                <a href={`${SITE}/pricing/`} onClick={closeMenu}>
                  Pricing
                </a>
              </li>
              <li>
                <a href={`${SITE}/blog/`} onClick={closeMenu}>
                  Blog
                </a>
              </li>
              <li>
                <a href={`${SITE}/contact/`} onClick={closeMenu}>
                  Contact
                </a>
              </li>
            </ul>
            <div className="auth-site-header__cta">
              <a className="auth-btn auth-btn--ghost" href={`${appBaseUrl}/login`}>
                Log In
              </a>
              <a className="auth-btn auth-btn--primary" href={`${appBaseUrl}/signup`}>
                Start free
              </a>
            </div>
          </div>
        </nav>
      </div>
    </header>
  );
}
